/**
 * The salon's promos (boona docs/codebase/widget-appearance/README.md): a row of pills on
 * the home, services and masters screens; a tap opens a popup with the picture and the
 * text. Nothing renders when the salon has none.
 */
import * as React from "react";
import { useTranslation } from "react-i18next";
import { ChevronRight, Coffee, Gift, Percent, Star, X } from "lucide-react";
import { cn } from "@/lib/utils";
import type { WidgetPromo } from "@/lib/types/company";
import { getPromos, mediaUrl } from "./widgetLook";

const ICONS: Record<string, React.ComponentType<{ className?: string }>> = {
  promotion: Percent,
  gift: Gift,
  coffee: Coffee,
  star: Star,
};

function PromoIcon({ icon, className }: { icon: string; className?: string }) {
  const Icon = ICONS[icon] ?? Percent;
  return <Icon className={className} />;
}

export function PromoModal({ promo, onClose }: { promo: WidgetPromo | null; onClose: () => void }) {
  const { t } = useTranslation();
  if (!promo) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" onClick={onClose} data-testid="promo-modal">
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" />
      <div
        role="dialog"
        aria-modal="true"
        aria-label={promo.title}
        className="relative z-10 w-full max-w-sm bg-card rounded-2xl shadow-2xl overflow-hidden animate-in fade-in zoom-in-95 duration-200 flex flex-col max-h-[85vh]"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          onClick={onClose}
          className="absolute right-3 top-3 z-10 p-2 rounded-full bg-card/90 hover:bg-card transition-colors"
          aria-label={t("widget.common.close", "Close")}
        >
          <X className="w-5 h-5 text-foreground" />
        </button>
        {promo.image ? (
          <img src={mediaUrl(promo.image)} alt="" className="w-full max-h-64 object-cover bg-muted shrink-0" />
        ) : (
          <div className="h-14 shrink-0" />
        )}
        <div className="overflow-y-auto p-6 pt-4">
          <h3 className="text-lg font-semibold text-foreground mb-2 pr-6">{promo.title}</h3>
          {promo.description ? (
            <p className="text-sm text-muted-foreground whitespace-pre-line">{promo.description}</p>
          ) : null}
        </div>
      </div>
    </div>
  );
}

/** The pill row. `promos` defaults to the salon's (from the widget settings). */
export function PromoPills({ promos, className }: { promos?: WidgetPromo[]; className?: string }) {
  const list = promos ?? getPromos();
  const [open, setOpen] = React.useState<WidgetPromo | null>(null);
  if (!list.length) return null;
  return (
    <>
      <div className={cn("-mx-6 px-6 flex gap-3 overflow-x-auto scrollbar-hide snap-x", className)} data-testid="promo-pills">
        {list.map((p) => (
          <button
            key={p.id}
            type="button"
            onClick={() => setOpen(p)}
            className={cn(
              "snap-start shrink-0 flex items-center gap-3 rounded-full px-4 py-3 text-left",
              "bg-primary/10 hover:bg-primary/15 transition-colors",
              list.length === 1 ? "w-full" : "max-w-[85%]",
            )}
          >
            <PromoIcon icon={p.icon} className="w-5 h-5 text-primary shrink-0" />
            <span className="flex-1 min-w-0 truncate text-sm font-medium text-foreground">{p.title}</span>
            <ChevronRight className="w-4 h-4 text-muted-foreground shrink-0" />
          </button>
        ))}
      </div>
      <PromoModal promo={open} onClose={() => setOpen(null)} />
    </>
  );
}
